// Kroki 1–6 lekcji v2 wyprowadzone z jednego rekordu `LessonV2` (SPEC §21.3, przebudowane w patch-45: §23.26). Krok 4 = pytania „Popatrz i wybierz” (`quiz`, po jednym na cel),
// krok 5 = luki (po jednej na cel, w zdaniu z lekcji), krok 6 = klocki ze zdań kroków 1 i 3 na zmianę z kartami „Co powiesz?”. Dzięki temu nie da się wprowadzić
// słowa „znikąd”. Czysty kod (bez słownika) — na jego wyniku zbuduje `buildCourseSession`.

import { chunkSentence } from './chunks.ts';
import { roleFor, voiceOf, type VoiceSlot } from './characters.ts';
import { lemmaCandidates, tokenize } from './lemma.ts';
import { wordImageId } from './imagePrompt.ts';
import { levelOfStage, type DialogueLine, type LessonTarget, type LessonV2 } from './levels.ts';
import { slug } from './text.ts';

export interface StepSentence {
  id: string;
  text: string;
  pl: string;
  /** Obrazek w ramce po prawej (krok 1 i 2 pokazują ten sam). */
  image: string;
}

/** Kwestia dialogu z rolą: `child` mówi dziecko, `bot` czyta druga postać; `voice` to głos POSTACI (nie roli) — SPEC §21.6. */
export interface SceneLine extends DialogueLine {
  id: string;
  role: 'child' | 'bot';
  voice: VoiceSlot;
}

export interface SceneTurn {
  id: string;
  /** Kwestia dziecka do powiedzenia (zadanie `speak`). */
  line: SceneLine;
  /** Kwestie drugiej osoby tuż przed nią (bot czyta je, tekst w dymku) — pusta, gdy dialog zaczyna się od dziecka. */
  cue: SceneLine[];
}

/**
 * Kotwica pamięciowa słowa przy pytaniu kroku 4 (SPEC §23.24, §23.26): obrazek TEGO słowa (id pliku `w-<słowo>`) albo — gdy słowa nie da się narysować —
 * kafelek z emoji celu i jednym zdaniem sytuacji po polsku. Cel bez kotwicy pokazuje samo emoji.
 */
export type StepAnchor = { kind: 'image'; image: string } | { kind: 'tile'; emoji: string; situationPl: string };

export function anchorOf(t: Pick<LessonTarget, 'word' | 'emoji' | 'anchor'>): StepAnchor {
  if (t.anchor?.draw) return { kind: 'image', image: wordImageId(t.word) };
  return { kind: 'tile', emoji: t.emoji ?? '🖼️', situationPl: t.anchor?.situationPl ?? '' };
}

/** Skąd pochodzi zdanie luki: kwestia dziecka (najlepiej — dziecko wpisuje słowo w swojej roli), zdanie opisu, zdanie wzbogacone albo kwestia drugiej osoby. */
export type ClozeSource = 'child' | 'describe' | 'enrich' | 'bot';

export interface ClozeStep {
  id: string;
  /** Cel (hasło), którego dotyczy luka. */
  word: string;
  pl: string;
  source: ClozeSource;
  /** Tekst zdania z luką: `before` + ___ + `after`. */
  before: string;
  answer: string;
  after: string;
  text: string;
  textPl: string;
  /** Id kroku, którego nagranie czyta to zdanie (`a1-001:s2`, `a1-001:d1`, `a1-001:e1`) — nagrania powstają dla każdego zdania lekcji. */
  audioFrom: string;
  /** Ostatnia kwestia drugiej osoby przed kwestią dziecka (pytanie do pokazania nad zdaniem); tylko dla `source: 'child'`. */
  cue?: SceneLine;
  /** Kto mówi zdanie z luką: postać dziecka (`child`) albo rozmówcy (`bot`); zdania opisu i wzbogacone nie mają mówcy (narrator). */
  who?: string;
}

/** Id nagrania pytania: zależy od TEKSTU, więc to samo pytanie (*What is this?*) ma jedno nagranie w całym kursie, a nie po jednym na cel. */
export const quizAudioId = (text: string): string => `q:${slug(text)}`;

/** Pytanie kroku 4. `options` w kolejności autorskiej: [dobra, błędne…] — losuje je `shuffleOptions`. */
export interface QuizStep {
  id: string;
  /** Cel, o który pyta pytanie (po odpowiedzi uczeń widzi go razem z `wordPl`). */
  word: string;
  wordPl: string;
  emoji?: string;
  /** Kotwica celu w polu obrazka: obrazek słowa albo kafelek. */
  anchor: StepAnchor;
  q: string;
  pl: string;
  answer: string;
  options: string[];
  /** Id nagrania pytania (`quizAudioId`). */
  audioFrom: string;
}

/** Jedna karta ze zdaniem na ekranie „Co powiesz?” (z nagraniem 🔊); `correct` tylko dla poprawnej odpowiedzi. */
export interface SayOptionStep {
  id: string;
  text: string;
  pl: string;
  who: string;
  voice: VoiceSlot;
  correct: boolean;
  /** Dlaczego ta opcja nie pasuje (tylko błędne). */
  whyPl?: string;
}

/** Karta „Co powiesz?”: `options` w kolejności autorskiej [poprawna, błędne…] — losuje je sesja; `accept` to zdania zaliczane (poprawne + warianty). */
export interface SayStep {
  id: string;
  word: string;
  situationPl: string;
  /** Kwestia rozmówcy nad kartami (angielski dymek z 🔊 i 🐢): id nagrania `<id kroku>-p`, głos postaci, która NIE mówi odpowiedzi (SPEC §23.35.6). */
  partner: { id: string; text: string; pl: string; who: string; voice: VoiceSlot };
  answer: SayOptionStep;
  options: SayOptionStep[];
  accept: string[];
}

export interface BuildStep {
  id: string;
  source: 'describe' | 'scene';
  text: string;
  pl: string;
  /** Kawałki w poprawnej kolejności (mieszanie robi sesja). */
  chunks: string[];
  /** Id kroku, którego nagranie czyta to zdanie: zdanie opisu, cała kwestia dziecka (gdy ma jedno zdanie) albo własne nagranie części kwestii wielozdaniowej. */
  audioFrom: string;
}

export interface LessonSteps {
  listen: StepSentence[];
  yourTurn: StepSentence[];
  scene: { setting: string; settingPl: string; learner: string; lines: SceneLine[]; turns: SceneTurn[] };
  cloze: ClozeStep[];
  quiz: QuizStep[];
  /** Karty „Co powiesz?” (puste, gdy lekcja ich nie ma). */
  say: SayStep[];
  build: BuildStep[];
}

export interface TargetHit {
  /** Pozycje znaków w tekście. */
  start: number;
  end: number;
  surface: string;
}

/** Pisownie tego samego słowa: cel „okay” pasuje też do „OK” w zdaniu (i odwrotnie). */
const SPELLING: Record<string, string> = { ok: 'okay' };
const canonical = (w: string): string => SPELLING[w] ?? w;

/** Znajduje cel (hasło, także wielowyrazowe) w tekście, uwzględniając odmianę („apples”, „drinks”) i dwie pisownie „okay”/„OK”. */
export function findTarget(text: string, target: string): TargetHit | null {
  const parts = target.toLowerCase().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  const toks = tokenize(text);
  for (let i = 0; i + parts.length <= toks.length; i++) {
    let ok = true;
    for (let k = 0; k < parts.length; k++) {
      const t = toks[i + k]!;
      const isLast = k === parts.length - 1;
      const same = isLast ? lemmaCandidates(t.lower).some((c) => canonical(c.lemma) === canonical(parts[k]!)) : canonical(t.lower) === canonical(parts[k]!);
      if (!same) {
        ok = false;
        break;
      }
    }
    if (ok) {
      const first = toks[i]!;
      const last = toks[i + parts.length - 1]!;
      const end = last.index + last.raw.length;
      return { start: first.index, end, surface: text.slice(first.index, end) };
    }
  }
  return null;
}

/** Id nagrania zdania z banku „Zmień zdanie”: `a1-001:a-apple-1`, `a1-001:a-ice-cream-2` (numeracja od 1). */
export const altId = (lessonId: string, word: string, index: number): string => `${lessonId}:a-${word.replace(/\s+/g, '-')}-${index + 1}`;

const wordCount = (text: string): number => tokenize(text).length;
const splitOn = (t: string): string[] => t.trim().split(/(?<=[.!?])\s+/).filter(Boolean);

/** Kwestia z kilkoma zdaniami → pary (zdanie, tłumaczenie) do klocków; gdy liczby zdań w EN i PL się różnią, zdań nie da się sparować i pomijamy je. */
export function splitSentences(text: string, pl: string): Array<{ text: string; pl: string }> {
  const en = splitOn(text);
  if (en.length <= 1) return [{ text, pl }];
  const p = splitOn(pl);
  return en.length === p.length ? en.map((x, i) => ({ text: x, pl: p[i]! })) : [];
}

/**
 * Lekcja z rolą dziecka dobraną do jego płci (patch-50): chłopiec gra Toma, dziewczynka Emmę (`roleFor`). Treść lekcji się nie zmienia — zmienia się tylko to,
 * które kwestie są „dziecka” (mówi je dziecko), a które „bota” (czyta je druga postać), a przez to kroki 3 (scenka), 5 (luki) i 6 (klocki). Bez zmiany roli zwraca tę samą lekcję.
 */
export function withRole(lesson: LessonV2, gender: string | undefined): LessonV2 {
  const role = roleFor(lesson.scene.learner, [...new Set(lesson.scene.dialogue.map((d) => d.who))], gender ?? '');
  return role === lesson.scene.learner ? lesson : { ...lesson, scene: { ...lesson.scene, learner: role, authoredLearner: lesson.scene.authoredLearner ?? lesson.scene.learner } };
}

export function sceneLines(lesson: LessonV2): SceneLine[] {
  return lesson.scene.dialogue.map((d, i) => ({ ...d, id: `${lesson.id}:s${i + 1}`, role: d.who === lesson.scene.learner ? 'child' : 'bot', voice: voiceOf(d.who) }));
}

/** „Co powiesz?”: odpowiedź mówi dziecko (druga postać), błędne opcje — osoba wskazana w danych; kolejność autorska [poprawna, błędne…]. */
export function saySteps(lesson: LessonV2): SayStep[] {
  return (lesson.say ?? []).map((item, i) => {
    const id = `${lesson.id}:y${i + 1}`;
    // „Co powiesz?” jest pisane dla roli autorskiej (rozmówcą jest druga postać, odpowiedź nagrana jej głosem) — niezależnie od płci dziecka; rola z konta dotyczy scenki i klocków (SPEC §23.29.6)
    const author = lesson.scene.authoredLearner ?? lesson.scene.learner;
    const answer: SayOptionStep = { id, text: item.answer.text, pl: item.answer.pl, who: author, voice: voiceOf(author), correct: true };
    const wrong = item.distractors.map((d, k): SayOptionStep => ({ id: `${id}-d${k + 1}`, text: d.text, pl: d.pl, who: d.who, voice: voiceOf(d.who), correct: false, whyPl: d.whyPl }));
    // rozmówca to druga postać dialogu (dla roli autorskiej Emmy — Tom), czyli ta, która nie mówi odpowiedzi; w dialogu bez drugiej postaci — Tom
    const partnerWho = lesson.scene.dialogue.map((d) => d.who).find((w) => w !== author) ?? 'Tom';
    const partner = { id: `${id}-p`, text: item.partner, pl: item.partnerPl, who: partnerWho, voice: voiceOf(partnerWho) };
    return { id, word: item.target, situationPl: item.situationPl, partner, answer, options: [answer, ...wrong], accept: [item.answer.text, ...(item.alsoOk ?? [])] };
  });
}

export function deriveSteps(lesson: LessonV2): LessonSteps {
  const d = lesson.describe;
  const listen: StepSentence[] = d.lines.map((l, i) => ({ id: `${lesson.id}:d${i + 1}`, text: l.text, pl: l.pl, image: d.image }));
  const yourTurn: StepSentence[] = lesson.enrich.lines.map((l, i) => ({ id: `${lesson.id}:e${i + 1}`, text: l.text, pl: l.pl, image: d.image }));

  const lines = sceneLines(lesson);
  const turns: SceneTurn[] = [];
  lines.forEach((line, i) => {
    if (line.role !== 'child') return;
    const cue: SceneLine[] = [];
    for (let j = i - 1; j >= 0 && lines[j]!.role === 'bot'; j--) cue.unshift(lines[j]!);
    turns.push({ id: line.id, line, cue });
  });

  // luki (krok 5): po jednej na cel. Najpierw cele w kwestiach dziecka (dziecko wpisuje słowo w swojej roli): po jednym celu na kwestię (różnorodność), potem reszta;
  // cel, którego nie ma w żadnej kwestii dziecka, dostaje zdanie z opisu, ze zdań wzbogaconych albo z kwestii drugiej osoby (w tej kolejności) — każdy cel ma lukę
  const picked: Array<{ lineIndex: number; word: string; hit: TargetHit }> = [];
  const usedWords = new Set<string>();
  const usedPairs = new Set<string>();
  for (const pass of [1, 2]) {
    lines.forEach((line, lineIndex) => {
      if (line.role !== 'child') return;
      const perLine = picked.filter((p) => p.lineIndex === lineIndex).length;
      if (pass === 1 && perLine > 0) return;
      for (const t of lesson.targets) {
        const key = `${lineIndex}|${t.word}`;
        if (usedWords.has(t.word) || usedPairs.has(key)) continue;
        const hit = findTarget(line.text, t.word);
        if (!hit) continue;
        picked.push({ lineIndex, word: t.word, hit });
        usedWords.add(t.word);
        usedPairs.add(key);
        if (pass === 1) break;
      }
    });
  }
  picked.sort((a, b) => a.lineIndex - b.lineIndex || a.hit.start - b.hit.start);
  const clozeId = (word: string): string => `${lesson.id}:g-${word.replace(/\s+/g, '-')}`;
  const gap = (word: string, pl: string, source: ClozeSource, text: string, textPl: string, hit: TargetHit, audioFrom: string, cue?: SceneLine, who?: string): ClozeStep => ({
    id: clozeId(word),
    word,
    pl,
    source,
    before: text.slice(0, hit.start),
    answer: hit.surface,
    after: text.slice(hit.end),
    text,
    textPl,
    audioFrom,
    ...(cue ? { cue } : {}),
    ...(who ? { who } : {}),
  });
  const cloze: ClozeStep[] = picked.map((p) => {
    const line = lines[p.lineIndex]!;
    const target = lesson.targets.find((t) => t.word === p.word)!;
    let cue: SceneLine | undefined;
    for (let j = p.lineIndex - 1; j >= 0; j--) if (lines[j]!.role === 'bot') { cue = lines[j]; break; }
    return gap(p.word, target.pl, 'child', line.text, line.pl, p.hit, line.id, cue, line.who);
  });
  const others: Array<{ source: ClozeSource; id: string; text: string; pl: string; who?: string }> = [
    ...d.lines.map((l, i) => ({ source: 'describe' as const, id: `${lesson.id}:d${i + 1}`, text: l.text, pl: l.pl })),
    ...lesson.enrich.lines.map((l, i) => ({ source: 'enrich' as const, id: `${lesson.id}:e${i + 1}`, text: l.text, pl: l.pl })),
    ...lines.filter((l) => l.role === 'bot').map((l) => ({ source: 'bot' as const, id: l.id, text: l.text, pl: l.pl, who: l.who })),
  ];
  for (const t of lesson.targets) {
    if (usedWords.has(t.word)) continue;
    const from = others.find((o) => findTarget(o.text, t.word));
    if (from) cloze.push(gap(t.word, t.pl, from.source, from.text, from.pl, findTarget(from.text, t.word)!, from.id, undefined, from.who));
  }

  // pytania kroku 4: po jednym na cel, z kotwicą celu w polu obrazka
  const quiz: QuizStep[] = (lesson.quiz ?? []).flatMap((item) => {
    const target = lesson.targets.find((t) => t.word.toLowerCase() === item.target.toLowerCase().trim());
    if (!target) return [];
    return [{
      id: `${lesson.id}:q-${target.word.replace(/\s+/g, '-')}`,
      word: target.word,
      wordPl: target.pl,
      ...(target.emoji ? { emoji: target.emoji } : {}),
      anchor: anchorOf(target),
      q: item.q,
      pl: item.pl,
      answer: item.answer,
      options: [item.answer, ...item.distractors],
      audioFrom: quizAudioId(item.q),
    }];
  });

  // klocki: zdania z kroku 1 oraz jednozdaniowe kwestie dziecka (3–10 słów na poziomach A, do 12 od B1)
  const maxBuild = levelOfStage(lesson.stage).startsWith('A') ? 10 : 12;
  const build: BuildStep[] = [
    ...d.lines.map((l, i): BuildStep => ({ id: `${lesson.id}:b-d${i + 1}`, source: 'describe', text: l.text, pl: l.pl, chunks: chunkSentence(l.text), audioFrom: `${lesson.id}:d${i + 1}` })),
    ...lines
      .filter((l) => l.role === 'child')
      .flatMap((l) => splitSentences(l.text, l.pl).map((x, k, all): BuildStep => {
        const id = all.length === 1 ? `${l.id}:b` : `${l.id}:b${k + 1}`;
        return { id, source: 'scene', text: x.text, pl: x.pl, chunks: chunkSentence(x.text), audioFrom: all.length === 1 ? l.id : id };
      }))
      .filter((b) => wordCount(b.text) >= 3 && wordCount(b.text) <= maxBuild),
  ];

  return {
    listen,
    yourTurn,
    scene: { setting: lesson.scene.setting, settingPl: lesson.scene.settingPl, learner: lesson.scene.learner, lines, turns },
    cloze,
    quiz,
    say: saySteps(lesson),
    build,
  };
}

// ---------------------------------------------------------------------------------------------
// Kolejność ćwiczeń i odpowiedzi — losowa (ziarno sesji), ale każdy cel dokładnie raz w kroku 4 i raz w kroku 5
// ---------------------------------------------------------------------------------------------

/** Losowa kolejność według generatora sesji (`rng` zwraca liczby z [0, 1)); nie zmienia wejścia. */
export function shuffleWith<T>(items: readonly T[], rng: () => number): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/** Losowa kolejność odpowiedzi pytania; `correct` to indeks dobrej odpowiedzi w zwróconej tablicy. */
export function shuffleOptions(step: Pick<QuizStep, 'options' | 'answer'>, rng: () => number = Math.random): { options: string[]; correct: number } {
  const options = shuffleWith(step.options, rng);
  return { options, correct: options.indexOf(step.answer) };
}

/** Teksty do nagrania (plan audio w patch-38): id, tekst, kto mówi i którym głosem. Wzbogacone zdania też — dziecko może chcieć usłyszeć wzór; pytania kroku 4 czyta głos główny (jedno nagranie na tekst pytania, wspólne dla całego kursu). */
export interface AudioText {
  id: string;
  text: string;
  kind: 'describe' | 'enrich' | 'dialogue' | 'target' | 'quiz' | 'alt' | 'say' | 'build';
  who?: string;
  /** Który głos ElevenLabs to nagra: „main” (lektor) albo „second” (druga postać) — src/lib/characters.ts. */
  voice: VoiceSlot;
  /** Dziecko słyszy to zdanie także wolniej (przycisk „Wolno”) — plan nagrań robi wtedy drugi plik `.slow`. */
  slow?: boolean;
  /**
   * Wolna wersja dodana w patchu 50 dla kwestii spoza roli, w której lekcja została napisana (rozmówca; kwestie drugiej roli w klockach) oraz w patchu 55 dla odpowiedzi „Co powiesz?” i pytań „Popatrz i wybierz”. Ikona 🐢 jest przy nich zawsze,
   * ale plan nagrań ich domyślnie nie robi (decyzja z 19.10.2026: „na razie bez tego”) — brak pliku = głos zapasowy z tym samym tekstem. Włączenie: `planCourseAudio(…, { partnerSlow: true })`, `npm run audio:generate -- --partner-slow`.
   */
  slowExtra?: boolean;
}
export function audioTexts(lesson: LessonV2): AudioText[] {
  const out: AudioText[] = [];
  lesson.describe.lines.forEach((l, i) => out.push({ id: `${lesson.id}:d${i + 1}`, text: l.text, kind: 'describe', voice: 'main', slow: true }));
  lesson.enrich.lines.forEach((l, i) => out.push({ id: `${lesson.id}:e${i + 1}`, text: l.text, kind: 'enrich', voice: 'main', slow: true }));
  const authored = lesson.scene.authoredLearner ?? lesson.scene.learner; // rola, w której lekcja jest napisana (nagrania „wolno” z patchów sprzed 50 dotyczyły tylko jej kwestii)
  lesson.scene.dialogue.forEach((l, i) => out.push({ id: `${lesson.id}:s${i + 1}`, text: l.text, kind: 'dialogue', who: l.who, voice: voiceOf(l.who), slow: true, ...(l.who === authored ? {} : { slowExtra: true }) })); // 🐢 jest przy każdej kwestii (też rozmówcy i drugiej roli); nagrywane domyślnie tylko kwestie roli autora
  for (const t of lesson.targets) out.push({ id: `${lesson.id}:t-${t.word.replace(/\s+/g, '-')}`, text: t.word, kind: 'target', voice: 'main' });
  const quizSeen = new Set<string>(); // to samo pytanie przy kilku celach (*What is this?*) ma jedno nagranie
  for (const q of lesson.quiz ?? []) {
    const id = quizAudioId(q.q);
    if (quizSeen.has(id)) continue;
    quizSeen.add(id);
    out.push({ id, text: q.q, kind: 'quiz', voice: 'main', slow: true, slowExtra: true }); // 🐢 przy pytaniu „Popatrz i wybierz” (patch 55); plik „wolno” poza planem domyślnym (`slowExtra`)
  }
  for (const t of lesson.targets) (t.alts ?? []).forEach((a, k) => out.push({ id: altId(lesson.id, t.word, k), text: a.text, kind: 'alt', voice: 'main' }));
  for (const st of saySteps(lesson)) {
    // kwestia rozmówcy ma 🔊 i 🐢 (jak każda kwestia rozmówcy: wolna wersja poza planem domyślnie — `slowExtra`)
    out.push({ id: st.partner.id, text: st.partner.text, kind: 'say', who: st.partner.who, voice: st.partner.voice, slow: true, slowExtra: true });
    for (const o of st.options) out.push({ id: o.id, text: o.text, kind: 'say', who: o.who, voice: o.voice, slow: true, slowExtra: true }); // 🐢 w każdym wierszu odpowiedzi (patch 55)
  }
  // części kwestii wielozdaniowych używane w klockach mają własne nagranie (dopisane na końcu, więc wcześniejsze id się nie zmieniają)
  // (kwestie obu postaci: dziecko gra Toma albo Emmę, więc klocki powstają z kwestii każdej z nich)
  const seen = new Set(out.map((o) => o.id));
  for (const role of [lesson.scene.learner, ...new Set(lesson.scene.dialogue.map((d) => d.who))]) {
    for (const b of deriveSteps({ ...lesson, scene: { ...lesson.scene, learner: role } }).build) {
      if (b.audioFrom !== b.id || seen.has(b.id)) continue;
      seen.add(b.id);
      out.push({ id: b.id, text: b.text, kind: 'build', voice: voiceOf(role), slow: true, ...(role === authored ? {} : { slowExtra: true }) });
    }
  }
  return out;
}
